// src/components/TrainerStatsModal.js
import { API_URL } from '../config/apiConfig';

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BarChartIcon from '@mui/icons-material/BarChart';
import PersonIcon from '@mui/icons-material/Person';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1]
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100]
}));

const TrainerCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
        boxShadow: theme.shadows[4]
    }
}));


export default function TrainerStatsModal({ open, onClose, affiliate, trainers }) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [statsData, setStatsData] = useState(null);
    const [expandedTrainers, setExpandedTrainers] = useState(new Set());

    // Load stats when modal opens or month changes
    useEffect(() => {
        if (open && affiliate?.id) {
            loadTrainerStats();
        }
    }, [open, affiliate?.id, currentMonth]);

    const loadTrainerStats = async () => {
        try {
            setLoading(true);
            setError('');

            const token = localStorage.getItem('token');
            const year = currentMonth.getFullYear();
            const month = currentMonth.getMonth() + 1; // getMonth() returns 0-11, we need 1-12

            const response = await fetch(`${API_URL}/trainer-schedule/stats/${affiliate.id}?year=${year}&month=${month}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to load trainer statistics');
            }

            const data = await response.json();
            setStatsData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleMonthChange = (direction) => {
        const newMonth = new Date(currentMonth);
        if (direction === 'prev') {
            newMonth.setMonth(newMonth.getMonth() - 1);
        } else {
            newMonth.setMonth(newMonth.getMonth() + 1);
        }
        setCurrentMonth(newMonth);
    };

    const toggleTrainerExpansion = (trainerId) => {
        const newExpanded = new Set(expandedTrainers);
        if (newExpanded.has(trainerId)) {
            newExpanded.delete(trainerId);
        } else {
            newExpanded.add(trainerId);
        }
        setExpandedTrainers(newExpanded);
    };

    const formatMonth = (date) => {
        return date.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
        });
    };

    const renderStatsTable = (data, title, showTrainerColumn = false) => {
        if (!data || Object.keys(data).length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography color="text.secondary">
                        No data available for this period
                    </Typography>
                </Box>
            );
        }

        return (
            <StyledTableContainer component={Paper}>
                {title && (
                    <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <BarChartIcon sx={{ mr: 1 }} />
                            {title}
                        </Typography>
                    </Box>
                )}
                <Table size="small">
                    <StyledTableHead>
                        <TableRow>
                            {showTrainerColumn && <TableCell><strong>Trainer</strong></TableCell>}
                            <TableCell><strong>Training Type</strong></TableCell>
                            <TableCell align="right"><strong>Classes Count</strong></TableCell>
                        </TableRow>
                    </StyledTableHead>
                    <TableBody>
                        {Object.entries(data).map(([key, value]) => {
                            if (showTrainerColumn) {
                                // For individual trainer data, key is trainingType, value is count
                                return (
                                    <TableRow key={key} hover>
                                        <TableCell component="th" scope="row">
                                            {title.replace(' Statistics', '')}
                                        </TableCell>
                                        <TableCell>{key}</TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold" color="primary">
                                                {value}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            } else {
                                // For total stats, key is trainingType, value is count
                                return (
                                    <TableRow key={key} hover>
                                        <TableCell component="th" scope="row">
                                            {key}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold" color="primary">
                                                {value}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                        })}
                    </TableBody>
                </Table>
            </StyledTableContainer>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { height: '90vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                        <BarChartIcon sx={{ mr: 1 }} />
                        Trainer Statistics
                    </Typography>
                    <IconButton onClick={onClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Month Navigation */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 2,
                    gap: 2
                }}>
                    <IconButton
                        onClick={() => handleMonthChange('prev')}
                        sx={{
                            bgcolor: 'grey.100',
                            '&:hover': { bgcolor: 'grey.200' }
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
                        {formatMonth(currentMonth)}
                    </Typography>

                    <IconButton
                        onClick={() => handleMonthChange('next')}
                        sx={{
                            bgcolor: 'grey.100',
                            '&:hover': { bgcolor: 'grey.200' }
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : statsData ? (
                    <Box>
                        {/* Total Statistics */}
                        <Box sx={{ mb: 4 }}>
                            {renderStatsTable(statsData.totalStats, 'Total Classes by Type')}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Individual Trainer Statistics */}
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1 }} />
                            Individual Trainer Statistics
                        </Typography>

                        {statsData.trainerStats && Object.keys(statsData.trainerStats).length > 0 ? (
                            Object.entries(statsData.trainerStats).map(([trainerName, trainerData]) => {
                                const isExpanded = expandedTrainers.has(trainerName);
                                const totalClasses = Object.values(trainerData).reduce((sum, count) => sum + count, 0);

                                return (
                                    <TrainerCard key={trainerName}>
                                        <CardContent sx={{ pb: '16px !important' }}>
                                            {/* Trainer Header */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    cursor: 'pointer',
                                                    mb: isExpanded ? 2 : 0
                                                }}
                                                onClick={() => toggleTrainerExpansion(trainerName)}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                    <Typography variant="h6">
                                                        {trainerName}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            ml: 2,
                                                            px: 1,
                                                            py: 0.5,
                                                            bgcolor: 'primary.light',
                                                            color: 'white',
                                                            borderRadius: 1,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        {totalClasses} classes total
                                                    </Typography>
                                                </Box>

                                                <IconButton size="small">
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Box>

                                            {/* Trainer Stats Table */}
                                            <Collapse in={isExpanded}>
                                                {renderStatsTable(trainerData, null)}
                                            </Collapse>
                                        </CardContent>
                                    </TrainerCard>
                                );
                            })
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">
                                    No trainer statistics available for this month
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="contained" color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}